import { Injectable } from "@angular/core";
import { uuid } from "src/app/support/uuid";
import { debounce, first } from "lodash-es";

export interface DetachedElementRefOptions {
  flex?: boolean;
}

export interface DetachedElementRef {
  element: HTMLElement;
  uuid: string;
  resizeObserver: ResizeObserver;
  options?: DetachedElementRefOptions;
}

@Injectable()
export class DetachedDomElementService {
  private map: Map<string, DetachedElementRef> = new Map();

  install(
    target: HTMLElement,
    options?: DetachedElementRefOptions
  ): DetachedElementRef {
    const elementUuid = uuid();

    const element = document.createElement("div");
    element.classList.add("absolute");

    if (options?.flex) {
      element.classList.add("flex");
    }

    const resizeObserver = new ResizeObserver(
      debounce((entries) => {
        const firstEntry = first(entries);
        if (!firstEntry) {
          return;
        }

        const { width, height, top, left } = target.getBoundingClientRect();

        if (!width || !height) {
          return;
        }

        element.style.width = `${width}px`;
        element.style.height = `${height}px`;
        element.style.top = `${top}px`;
        element.style.left = `${left}px`;
      }, 500)
    );

    resizeObserver.observe(target);

    const detachedElementRef: DetachedElementRef = {
      element,
      resizeObserver,
      uuid: elementUuid,
      options
    };

    this.map.set(elementUuid, detachedElementRef);

    document.body.appendChild(element);

    return detachedElementRef;
  }

  show(elementUuid: string): void {
    const detachedElementRef = this.map.get(elementUuid);
    if (!detachedElementRef) {
      console.warn(
        `DetachedDomElementService::show undefined detachedElementRef key: "${elementUuid}"`
      );
      return;
    }

    if (detachedElementRef.options?.flex) {
      detachedElementRef.element.style.display = "";
      detachedElementRef.element.classList.add("flex");
    } else {
      detachedElementRef.element.style.display = "block";
    }
  }

  hide(elementUuid: string): void {
    const detachedElementRef = this.map.get(elementUuid);
    if (!detachedElementRef) {
      console.warn(
        `DetachedDomElementService::hide undefined detachedElementRef key: "${elementUuid}"`
      );
      return;
    }

    if (detachedElementRef.options?.flex) {
      detachedElementRef.element.classList.remove("flex");
    }

    detachedElementRef.element.style.display = "none";
  }

  destory(elementUuid: string): void {
    const detachedElementRef = this.map.get(elementUuid);
    if (!detachedElementRef) {
      console.warn(
        `DetachedDomElementService::destroy undefined detachedElementRef key: "${elementUuid}"`
      );
      return;
    }

    detachedElementRef.element.remove();
    this.map.delete(elementUuid);
  }
}
